import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import { useUpdateVanQuestionnaireStore } from '@/stores/useUpdateVanQuestionnaire';
import { CreditDetails as CreditDetailsType, Maybe } from '@/graphql/__generated__/van-credits';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface CreditDetailsProps {
	data: Maybe<CreditDetailsType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	vehiclesRequested = 'vehicles_requested',
	term = 'term',
	downPaymentPercentage = 'down_payment_percentage',
	downPaymentAmount = 'down_payment_amount',
	expectedAdditionalIncome = 'expected_additional_income',
	amountRequested = 'amount_requested'
}

const CreditDetails: React.FC<CreditDetailsProps> = ({ data, title, isEditing, inputErrors }) => {
	const t = useTranslations('VanCreditRequestQuestionnaire');
	const { creditDetails, updateCreditDetails } = useUpdateVanQuestionnaireStore();
	const [formData, setFormData] = useState(creditDetails as CreditDetailsType);
	const [percentageValue, setPercentageValue] = useState<string>('');
	const [amountValue, setAmountValue] = useState<string | null>(null);
	const [inputKey, setInputKey] = useState<number>(0);

	useEffect(() => {
		if (!isEditing) {
			setFormData(creditDetails);
			setPercentageValue(creditDetails.downPaymentPercentage?.toString() || '');
			setAmountValue(creditDetails.downPaymentAmount?.toString() || '');
		} else {
			if (!percentageValue && !amountValue && data) {
				setPercentageValue(data?.downPaymentPercentage?.toString() || '');
				setAmountValue(data?.downPaymentAmount?.toString() || '');
			}
		}
	}, [creditDetails, isEditing, data]);

	const handleChange = (key: string, value: any) => {
		if (key === 'downPaymentPercentage') {
			let stringValue = value?.toString() || '';
			stringValue = stringValue.replace('%', '').trim();

			if (stringValue === '' || stringValue === '.' || /^[\d.]*$/.test(stringValue)) {
				setPercentageValue(stringValue);

				setAmountValue(null);
				setInputKey((prev) => prev + 1);
				setFormData((prevData: any) => ({
					...prevData,
					downPaymentAmount: null,
					downPaymentPercentage: stringValue === '' ? null : stringValue
				}));
				updateCreditDetails({
					downPaymentAmount: null,
					downPaymentPercentage: stringValue === '' ? null : stringValue
				});
			}
			return;
		}

		if (key === 'downPaymentAmount') {
			const processedValue = value;
			setAmountValue(processedValue);

			if (processedValue && processedValue !== '') {
				setPercentageValue('');
				setFormData((prevData: any) => ({
					...prevData,
					downPaymentPercentage: null,
					[key]: processedValue
				}));
				updateCreditDetails({
					downPaymentPercentage: null,
					[key]: processedValue
				});
			} else {
				setFormData((prevData: any) => ({
					...prevData,
					[key]: processedValue
				}));
				updateCreditDetails({ [key]: processedValue });
			}
			return;
		}

		const processedValue = value;
		setFormData((prevData: any) => ({
			...prevData,
			[key]: processedValue
		}));
		updateCreditDetails({ [key]: processedValue });
	};

	const handleBlur = (key: string, value: any) => {
		if (key === 'downPaymentPercentage') {
			const stringValue = value?.toString() || '';
			if (stringValue === '' || stringValue === '.') {
				setPercentageValue('');
				updateCreditDetails({ [key]: null });
				return;
			}

			const numericValue = parseFloat(stringValue);
			if (!isNaN(numericValue)) {
				if (numericValue < 10 || numericValue > 90) {
					const clampedValue = Math.max(10, Math.min(90, numericValue));
					const finalValue = clampedValue.toString();
					setPercentageValue(finalValue);
					updateCreditDetails({ [key]: clampedValue.toString() });
				} else {
					updateCreditDetails({ [key]: numericValue.toString() });
				}
			}
		}
	};

	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'vehiclesRequested'}
						value={
							formData['vehiclesRequested'] !== null
								? formData['vehiclesRequested']
								: data?.vehiclesRequested || ''
						}
						onChange={(newValue) => handleChange('vehiclesRequested', newValue)}
						error={inputErrors?.[inputErrorMapping.vehiclesRequested] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'amountRequested'}
						value={formData['amountRequested'] || data?.amountRequested || ''}
						onChange={(newValue) => handleChange('amountRequested', newValue)}
						error={inputErrors?.[inputErrorMapping.amountRequested] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						key={`downPaymentAmount-${inputKey}`}
						title={'downPaymentAmount'}
						value={amountValue !== '' ? amountValue : data?.downPaymentAmount || ''}
						type="money"
						onChange={(newValue) => handleChange('downPaymentAmount', newValue)}
						error={inputErrors?.[inputErrorMapping.downPaymentAmount] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'downPaymentPercentage'}
						value={percentageValue}
						onChange={(newValue) => handleChange('downPaymentPercentage', newValue)}
						onBlur={(newValue) => handleBlur('downPaymentPercentage', newValue)}
						error={inputErrors?.[inputErrorMapping.downPaymentPercentage] || ''}
						type="number"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'term'}
						value={formData['term'] !== null ? formData['term'] : data?.term || ''}
						onChange={(newValue) => handleChange('term', newValue)}
						error={inputErrors?.[inputErrorMapping.term] || ''}
						type="number"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'expectedAdditionalIncome'}
						value={formData['expectedAdditionalIncome'] || data?.expectedAdditionalIncome || ''}
						onChange={(newValue) => handleChange('expectedAdditionalIncome', newValue)}
						error={inputErrors?.[inputErrorMapping.expectedAdditionalIncome] || ''}
						type="money"
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			<div className="flex flex-row flex-wrap w-full">
				{isEditing ? (
					editableFields()
				) : (
					<>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'vehiclesRequested'}
								value={formatValue(data?.vehiclesRequested, t, 'vehiclesRequested')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'amountRequested'}
								value={formatValue(data?.amountRequested, t, 'amountRequested')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'downPaymentAmount'}
								value={formatValue(data?.downPaymentAmount, t, 'downPaymentAmount')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'downPaymentPercentage'}
								value={formatValue(data?.downPaymentPercentage, t, 'downPaymentPercentage')}
							/>
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput title={'term'} value={formatValue(data?.term, t, 'term')} />
						</div>
						<div className="w-1/2 px-3 py-1">
							<QuestionnaireInput
								title={'expectedAdditionalIncome'}
								value={formatValue(data?.expectedAdditionalIncome, t, 'expectedAdditionalIncome')}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default CreditDetails;
